import { useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonIcon,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSelect,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { ComplexQuestionEditorProps, OpsanioDnsEntry } from "../../../";
import { useAnketa } from "../../context_provider";
import { useAnketaI18n } from "src/internationalization";

// Common DNS record types offered in the "Type" select. Kept intentionally
// small; the value is stored verbatim so new types can be added here without
// touching the parser.
const DNS_RECORD_TYPES = [
  "A",
  "AAAA",
  "CNAME",
  "MX",
  "TXT",
  "NS",
  "PTR",
  "SRV",
];

const emptyEntry = (): OpsanioDnsEntry => ({ name: "", type: "A", ip: "" });

export const ComplexQuestionDnsEntriesEditor = ({
  question,
  setValue,
  invalid,
}: ComplexQuestionEditorProps): JSX.Element => {
  const ctx = useAnketa();
  const { translate } = useAnketaI18n();

  const [entries, setEntries] = useState<OpsanioDnsEntry[]>([]);

  // Load the persisted JSON-stringified array once on mount.
  useEffect(() => {
    const raw = question.fact.getStrValue(ctx.i18nContext);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setEntries(
          parsed.map((e: any) => ({
            name: String(e?.name ?? ""),
            type: String(e?.type ?? "A"),
            ip: String(e?.ip ?? ""),
          })),
        );
      }
    } catch {
      // not JSON, ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (updated: OpsanioDnsEntry[]) => {
    setEntries(updated);
    setValue(JSON.stringify(updated));
  };

  const addEntry = () => persist([...entries, emptyEntry()]);

  const removeEntry = (index: number) =>
    persist(entries.filter((_, i) => i !== index));

  const updateEntry = (
    index: number,
    field: keyof OpsanioDnsEntry,
    value: string,
  ) =>
    persist(
      entries.map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry,
      ),
    );

  const typeOptions = DNS_RECORD_TYPES.map((t) => ({ value: t, text: t }));

  return (
    <EuiFlexItem grow style={{ minWidth: "200px" }}>
      {entries.length === 0 && (
        <EuiText size="s" color="subdued">
          {translate("anketa.documentation.opsanio.network.dns.entries.empty", {
            defaultMessage: "No DNS entries yet.",
          })}
        </EuiText>
      )}

      {entries.map((entry, i) => (
        <div key={i}>
          <EuiFlexGroup gutterSize="s" alignItems="flexEnd" responsive={false}>
            <EuiFlexItem>
              <EuiFormRow
                label={translate(
                  "anketa.documentation.opsanio.network.dns.entries.name",
                  { defaultMessage: "Name" },
                )}
                display="rowCompressed"
              >
                <EuiFieldText
                  compressed
                  value={entry.name}
                  isInvalid={invalid}
                  disabled={question.readonly}
                  onChange={(e) => updateEntry(i, "name", e.target.value)}
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem grow={false} style={{ minWidth: "110px" }}>
              <EuiFormRow
                label={translate(
                  "anketa.documentation.opsanio.network.dns.entries.type",
                  { defaultMessage: "Type" },
                )}
                display="rowCompressed"
              >
                <EuiSelect
                  compressed
                  options={typeOptions}
                  value={entry.type}
                  disabled={question.readonly}
                  onChange={(e) => updateEntry(i, "type", e.target.value)}
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow
                label={translate(
                  "anketa.documentation.opsanio.network.dns.entries.ip",
                  { defaultMessage: "IP Address" },
                )}
                display="rowCompressed"
              >
                <EuiFieldText
                  compressed
                  value={entry.ip}
                  isInvalid={invalid}
                  disabled={question.readonly}
                  onChange={(e) => updateEntry(i, "ip", e.target.value)}
                />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonIcon
                iconType="trash"
                color="danger"
                display="base"
                disabled={question.readonly}
                aria-label={translate(
                  "anketa.documentation.opsanio.network.dns.entries.remove",
                  { defaultMessage: "Remove entry" },
                )}
                onClick={() => removeEntry(i)}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size="s" />
        </div>
      ))}

      <EuiSpacer size="xs" />
      <EuiFlexGroup responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            iconType="plusInCircle"
            disabled={question.readonly}
            onClick={addEntry}
          >
            {translate("anketa.documentation.opsanio.network.dns.entries.add", {
              defaultMessage: "Add DNS entry",
            })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFlexItem>
  );
};
